import type { GetStaticPaths, GetStaticProps } from "next";
import Head from "next/head";
import { FC, useCallback, useEffect, useState } from "react";
import MainLayout from "../../components/MainLayout";
import {
  CITY_FILE,
  FOOTER_FILE,
  Home_File,
  LEGAL_CONSULTATION_FILE,
  MENUS_FILE,
  STATE_FILE,
  CITY_FILE_STAGING
} from "../../constants/file-paths";
import Menus from "../../dtos/Menus.dto";
import { promises as fs } from "fs";
import FooterDefinition from "../../dtos/Footer.dto";
import CorneredBox from "../../components/CorneredBox";
import { CardStyled } from "../../components/Card.styled";
import { Row, Col, InputGroup, Form, Button } from "react-bootstrap";
import { FormControlStyled } from "../../components/FormControl.styled";
import Link from "next/link";
import { LinkStyled } from "../../components/Link.styled";
import IHome, { ICity, IState } from "../../dtos/Home.dto";
import { allConcernedAreas } from "../../constants/concernedAreasForLegalConsultation";
import LeadCityLawyerForm from "../../components/CityLawyer/LeadCityLawyerForm";
import Text from "../../styled/Text";
import setTrackingData from "../../utils/tracking";
import { useRouter } from "next/router";
import useResponsiveDevice from "../../components/useResponsiveDevice";
import { useDispatch } from "react-redux";
import { titleCase } from "../../utils/titleCase";
import FindAllLawyersHeader from "../../components/CityLawyer/FindAllLawyersHeader";
import { ButtonStyled } from "../../components/Button.styled";
import StickyFooterCityLawyers from "../../components/StickyFooterCityLawyers/index";
import { ParsedUrlQuery } from "querystring";
import Breadcrumb, { BreadcrumbItem } from "../../components/Breadcrumb";
import HeadingText1 from "../../components/HeadingText1";
import HeadingText2 from "../../styled/HeadingText2";

function getSchema(o: any) {
  return (
    <script
      type="application/ld+json"
      dangerouslySetInnerHTML={{ __html: JSON.stringify(o) }}
    />
  );
}

const ConcernArea: FC<IHome> = ({
  menus,
  HomePageData,
  footer,
  locations,
  concernedAreas,
  product,
  CharArray,
}) => {
  const {
    page: { home_page },
    page: { seo },
  } = HomePageData;

  const [state, setState] = useState("");
  const [city, setCity] = useState("");
  const [search, setSearch] = useState({
    temp: "",
    value: "",
  });
  const [concernedArea, setConcernedArea] = useState("");
  const [char, setChar] = useState("");
  const { isMobile } = useResponsiveDevice();
  const router = useRouter();
  const dispatch: any = useDispatch();
  const [showingNumber, setShowingNumber] = useState(4242);
  const [schema, setSchema] = useState("");

  const breadcrumbItems: BreadcrumbItem[] = [
    {
      label: "Home",
      link: "/",
    },
    {
      label: "Find Lawyers",
      link: "/find-all-lawyers-in-cities",
    },
    {
      label: `Find${concernedArea ? ` ${concernedArea}` : ""} Lawyers`,
      link: null,
    },
  ];

  useEffect(() => {
    setShowingNumber(50);
  }, []);
  const handleStateChange = (e: any) => {
    setState(e.target.value);
    setCity("");
  };

  const handleCityChange = (e: any) => {
    setCity(e.target.value);
  };

  const handleSearchChange = (e: any) => {
    setSearch((prev) => ({
      ...prev,
      temp: e.target.value,
    }));
  };

  const handleCharChange = (CHAR: string) => {
    setCity("");
    setChar(CHAR);
  };

  const handleConcernedAreaChange = (e: any) => {
    const concer_area = e.target.value.replace(/ /g, "-").toLowerCase();
    router.push(`/find-all-lawyers-in-cities/${concer_area}`);
  };

  useEffect(() => {
    if (search.temp === "") setSearch({ temp: "", value: "" });
  }, [search.temp]);

  useEffect(() => {
    if (!router.isReady) return;
    setTrackingData(dispatch, isMobile, router);
  });

  useEffect(() => {
    if (router.query?.concern_area) {
      const tempConcernArea = (router.query?.concern_area as string) || "";
      let finalConcernArea = tempConcernArea.replace(/-/g, " ");
      finalConcernArea = titleCase(finalConcernArea);
      let index = concernedAreas.findIndex(
        (i) =>
          finalConcernArea.toLowerCase() === i.toLowerCase() ||
          tempConcernArea.toLowerCase() === i.toLowerCase()
      );
      finalConcernArea = concernedAreas[index];
      setConcernedArea(finalConcernArea as string);
    }
  }, [concernedAreas, router.query]);
  const schemaObject: any = {
    "@context": "https://schema.org",
    "@type": "Product",
    name: `${titleCase(
      String(router.query?.concern_area).replace(/-/g, " ")
    )} Lawyers and Legal Advisors in India | ezyLegal`,
    image:
      "https://cms.ezylegal.in/wp-content/uploads/2021/06/cases_black_24dp.svg",
    description: `ezyLegal's Lawyers search tool helps you find ${titleCase(
      String(router.query?.concern_area).replace(/-/g, " ")
    )} legal advisor basis your need in your location. We have a ${titleCase(
      String(router.query?.concern_area).replace(/-/g, " ")
    )} lawyers from every nook and corner of India.`,
    aggregateRating: {
      "@type": "AggregateRating",
      ratingValue: "4.8",
      ratingCount: "83",
      worstRating: "1",
      bestRating: "5",
    },
  };

  const showingNumberMoreThanCityLawyers =
    locations.cities.filter((City: ICity) =>
      char
        ? char === City.name.charAt(0)
        : search.value
        ? City.name.toLowerCase().includes(search.value.toLowerCase())
        : state
        ? city
          ? city === City.name && state === City.stateCode
          : state === City.stateCode
        : city
        ? city === City.name
        : true
    ).length > showingNumber;

  const MapCityLawyersOptions = useCallback(() => {
    return (
      <>
        {locations.cities
          .filter((City: ICity) =>
            state
              ? city
                ? city === City.name && state === City.stateCode
                : state === City.stateCode
              : true
          )
          .map((city: ICity) => (
            <option key={`${city.stateCode}-${city.name}`} value={city.name}>
              {city.name}
            </option>
          ))}
      </>
    );
  }, [state, city, locations.cities]);

  const MapCityLawyers = useCallback(() => {
    return (
      <>
        {locations.cities
          .filter((City: ICity) =>
            char
              ? char === City.name.charAt(0)
              : search.value
              ? City.name.toLowerCase().includes(search.value.toLowerCase())
              : state
              ? city
                ? city === City.name && state === City.stateCode
                : state === City.stateCode
              : city
              ? city === City.name
              : true
          )
          .slice(0, showingNumber)
          .map((city: ICity) => (
            <Col
              key={`${city.stateCode}-${city.name}`}
              xs={6}
              sm={6}
              md={4}
              className="mb-2"
            >
              <Link
                href={{
                  pathname: `/find-${
                    router.query?.concern_area
                  }-lawyers-in-${city.name.toLowerCase().replace(/ /g, "-")}`,
                }}
                passHref
                legacyBehavior
              >
                <LinkStyled fontSize="base">
                  {router.query?.concern_area
                    ? router.query?.concern_area == "rera-act"
                      ? "RERA Act "
                      : router.query?.concern_area == "nclt-matter"
                      ? "NCLT Matter "
                      : router.query?.concern_area == "gst"
                      ? "GST "
                      : router.query?.concern_area == "alimony-and-maintenance"
                      ? "Alimony and Maintenance "
                      : router.query?.concern_area == "investment-and-fundraise"
                      ? "Investment and Fundraise "
                      : router.query?.concern_area == "labour-and-employment"
                      ? "Labour and Employment "
                      : router.query?.concern_area == "trademark-and-copyrights"
                      ? "Trademark and Copyrights "
                      : `${titleCase(
                          String(router.query?.concern_area).replace(/-/g, " ")
                        )} `
                    : ""}
                  Lawyers in {city.name}
                </LinkStyled>
              </Link>
            </Col>
          ))}

        <Col xs={12} className="pt-2 border-top">
          <ButtonStyled
            backgroundColor="#396AE8"
            color="#fff"
            className="d-flex align-items-center justify-content-center"
            onClick={() => {
              showingNumberMoreThanCityLawyers
                ? setShowingNumber(showingNumber + 50)
                : setShowingNumber(50);
            }}
          >
            <Text className="mb-0">
              {showingNumberMoreThanCityLawyers ? "Show More" : "Show Less"}
            </Text>
            <div
              className="d-flex align-items-center justify-content-center ml-2"
              style={{
                transform: !showingNumberMoreThanCityLawyers
                  ? "rotate(-90deg)"
                  : "rotate(90deg)",
              }}
            >
              <img
                src="/city-lawyers/icons/caretr.svg"
                alt="see-more-less-button"
                height={10}
                width={10}
              />
            </div>
          </ButtonStyled>
        </Col>
      </>
    );
  }, [
    locations.cities,
    search.value,
    state,
    city,
    router.query,
    char,
    showingNumber,
    showingNumberMoreThanCityLawyers,
  ]);

  const MapConcernedAreas = useCallback(() => {
    return (
      <>
        {concernedAreas.map((CONCERNED_AREA) => (
          <Col key={CONCERNED_AREA} xs={6} sm={6} md={4} className="mb-2">
            <Link
              href={{
                pathname: `/find-all-lawyers-in-cities/${CONCERNED_AREA.toLowerCase().replace(
                  / /g,
                  "-"
                )}`,
              }}
              passHref
              legacyBehavior
            >
              <Button variant="text" className="p-0 text-left">
                <LinkStyled fontSize="base">{CONCERNED_AREA}</LinkStyled>
              </Button>
            </Link>
          </Col>
        ))}
      </>
    );
  }, [concernedAreas]);
  return (
    <MainLayout menus={menus} headerBgColorBack="skyBlue" footer={footer}>
      <Head>
        {getSchema(schemaObject)}
        <link
          rel="canonical"
          href={`https://www.ezylegal.in/city-lawyers${router?.asPath}`}
        />
        <meta name="robots" content="index, follow" />
        <meta name="copyright" content="ezyLegal" />
        <meta name="reply-to" content={"contact@ezylegal.in"} />
        <meta property="og:type" content="product" />
        <meta
          property="og:url"
          content={`https://www.ezylegal.in/city-lawyers${router?.asPath}`}
        />
        <meta
          property="og:image"
          content="https://ezylegal.in/images/EzyLegal_final_logo-Kit-08_128x128.jpg"
        />
        <meta
          property="og:title"
          content={`${
            concernedArea == "NCLT matter" ? "NCLT Matter" : concernedArea
          } Lawyers and Legal Advisors in India cdfvf| ezyLegal`}
        />
        <meta
          property="og:description"
          content={`ezyLegal's Lawyers search tool helps you find ${
            concernedArea == "NCLT matter" ? "NCLT Matter" : concernedArea
          } legal advisor basis your need in your location. We have a ${
            concernedArea == "NCLT matter" ? "NCLT Matter" : concernedArea
          } lawyers from every nook and corner of India.`}
        />
        <meta
          property="title"
          content={`${
            concernedArea == "NCLT matter" ? "NCLT Matter" : concernedArea
          } Lawyers and Legal Advisors in India | ezyLegal`}
        />
        <meta
          name="description"
          content={`ezyLegal's Lawyers search tool helps you find ${
            concernedArea == "NCLT matter" ? "NCLT Matter" : concernedArea
          } legal advisor basis your need in your location. We have a ${
            concernedArea == "NCLT matter" ? "NCLT Matter" : concernedArea
          } lawyers from every nook and corner of India.`}
        />
        <title>
          {`${
            concernedArea == "NCLT matter" ? "NCLT Matter" : concernedArea
          } Lawyers and Legal Advisors in India | ezyLegal`}
        </title>
        <meta
          name="keywords"
          content={`find lawyers in cities, find ${String(
            router.query?.concern_area
          ).replace(/-/g, " ")} lawyers in cities`}
        />
      </Head>
      {/* @ts-ignore */}
      <FindAllLawyersHeader product={product} city={''} concernedArea={concernedArea} />
      <CorneredBox
        bgColor="lightSkyBlue"
        marginTop="-25px"
        paddingTop="20px"
        zIndex={15}
        position="relative"
      >
        <div className="p-4">
          <Breadcrumb items={breadcrumbItems} />

          <Row className="flex-row-reverse">
            <Col xs={12} md={5}>
              <Row
                style={{
                  position: "sticky",
                  top: 20,
                  maxHeight: isMobile ? "unset" : "95vh",
                  overflowY: "auto",
                }}
                className="mb-3"
              >
                <Col>
                  <CardStyled className="shadow-sm">
                    <CardStyled.Header className="d-flex align-items-center justify-content-center">
                      <Text className="mb-0 underline" fontSize="lg" weight="semibold">
                        Ask Lawyer
                      </Text>
                    </CardStyled.Header>
                    <CardStyled.Body>
                      <LeadCityLawyerForm />
                    </CardStyled.Body>
                  </CardStyled>
                </Col>
              </Row>
            </Col>
            <Col xs={12} md={7}>
              <CardStyled className="shadow-sm">
                <CardStyled.Header>
                  <CardStyled.Title>
                    <HeadingText1 fontSize="lg" weight="bold">
                      {`Find${
                        concernedArea ? ` ${concernedArea}` : ""
                      } Lawyers in all cities`}
                    </HeadingText1>
                  </CardStyled.Title>
                  <InputGroup>
                    <FormControlStyled
                      placeholder="Search..."
                      value={search.temp}
                      onKeyDown={(e: any) => {
                        if (e.key === "Enter") {
                          setSearch((prev) => ({
                            ...prev,
                            value: search.temp,
                          }));
                        }
                      }}
                      onChange={handleSearchChange}
                    />
                    <InputGroup.Append style={{ overflow: "hidden" }}>
                      <InputGroup.Text
                        style={{ cursor: "pointer" }}
                        onClick={() => {
                          setSearch((prev) => ({
                            ...prev,
                            value: search.temp,
                          }));
                        }}
                      >
                        <img
                          src="/city-lawyers/icons/search-fill.svg"
                          alt="search"
                          width={20}
                          height={20}
                        />
                      </InputGroup.Text>
                    </InputGroup.Append>
                  </InputGroup>
                  <Row className="mt-2">
                    <Col xs={12} lg={4}>
                      <Form.Group>
                        <Form.Label>State</Form.Label>
                        <FormControlStyled
                          as="select"
                          className="mr-2 w-100"
                          value={state}
                          onChange={handleStateChange}
                        >
                          <option value="">Select State</option>
                          {locations.states.map((state: IState) => (
                            <option key={state.isoCode} value={state.isoCode}>
                              {state.name}
                            </option>
                          ))}
                        </FormControlStyled>
                      </Form.Group>
                    </Col>
                    <Col xs={12} lg={4}>
                      <Form.Group>
                        <Form.Label>City</Form.Label>
                        <FormControlStyled
                          as="select"
                          className="mr-2 w-100"
                          value={city}
                          onChange={handleCityChange}
                        >
                          <option value="">Select City</option>
                          <MapCityLawyersOptions />
                        </FormControlStyled>
                      </Form.Group>
                    </Col>
                    <Col xs={12} lg={4}>
                      <Form.Group>
                        <Form.Label>Practice Area</Form.Label>
                        <FormControlStyled
                          as="select"
                          className="mr-2 w-100"
                          value={concernedArea}
                          onChange={handleConcernedAreaChange}
                        >
                          <option value="">Select Practice Area</option>
                          {concernedAreas.map((concernedArea: string) => (
                            <option key={concernedArea} value={concernedArea}>
                              {concernedArea}
                            </option>
                          ))}
                        </FormControlStyled>
                      </Form.Group>
                    </Col>
                  </Row>
                  <Row>
                    <Col xs={12} className="mb-2">
                      <Form.Label>Find Lawyers In Your City</Form.Label>
                    </Col>
                    <Col>
                      <Row className="px-3">
                        {CharArray.map((CHAR) => (
                          <Col
                            key={CHAR.label}
                            xs="auto"
                            className="mx-1 mb-1 p-0"
                          >
                            <ButtonStyled
                              borderRadius="100px"
                              backgroundColor={
                                char === CHAR.value ? "#396AE8" : "#FFF"
                              }
                              paddingBottom="0px"
                              auto
                              className="shadow-sm"
                              style={{
                                width: 40,
                                height: 40,
                                transition: ".15s",
                              }}
                              onClick={() => handleCharChange(CHAR.value)}
                            >
                              <Text
                                weight="semibold"
                                color={char === CHAR.value ? "white" : "black"}
                              >
                                {CHAR.label}
                              </Text>
                            </ButtonStyled>
                          </Col>
                        ))}
                      </Row>
                    </Col>
                  </Row>
                </CardStyled.Header>
                <CardStyled.Body>
                  <Row>
                    <Col xs={12} className="mb-2">
                      <Text weight="bold" fontSize="lg">
                        {CharArray.find((CHAR) => CHAR.value === char)?.label ||
                          ""}
                      </Text>
                    </Col>
                    <MapCityLawyers />
                  </Row>
                </CardStyled.Body>
              </CardStyled>
              <CardStyled className="mt-3 shadow-sm">
                <CardStyled.Header>
                  <HeadingText2 fontSize="lg" weight="bold">
                    Find Lawyers by Practice Area
                  </HeadingText2>
                </CardStyled.Header>
                <CardStyled.Body>
                  <Row>
                    <MapConcernedAreas />
                  </Row>
                </CardStyled.Body>
              </CardStyled>
            </Col>
          </Row>
        </div>
      </CorneredBox>
      {isMobile && <StickyFooterCityLawyers data={product} />}
    </MainLayout>
  );
};

export const getStaticPaths: GetStaticPaths = async () => {
  const concernedAreas = allConcernedAreas;

  let paths = [];
  for (const concern_area of concernedAreas) {
    paths.push({
      params: {
        concern_area: `${concern_area.toLowerCase().replace(/ /g, "-")}`,
        name: concern_area,
      },
    });
  }

  return {
    paths,
    fallback: false,
  };
};

interface IParams extends ParsedUrlQuery {
  concern_area: string;
}

export const getStaticProps: GetStaticProps = async ({ params }) => {
  const { concern_area } = params as IParams;

  const menuData = await fs.readFile(MENUS_FILE, { encoding: "utf-8" });
  const menus: Menus = JSON.parse(menuData);

  const Homepage = await fs.readFile(Home_File, { encoding: "utf-8" });
  const HomePageData: any = JSON.parse(Homepage);

  const footerData = await fs.readFile(FOOTER_FILE, { encoding: "utf-8" });
  const footer: FooterDefinition = JSON.parse(footerData);

  const statesData = await fs.readFile(STATE_FILE, { encoding: "utf-8" });
  const statesAllCountries: IState[] = JSON.parse(statesData);

  const citiesData = await fs.readFile(CITY_FILE_STAGING, { encoding: "utf-8" });
  const citiesAllCountries: ICity[] = JSON.parse(citiesData);

  const legalConsultationData = await fs.readFile(LEGAL_CONSULTATION_FILE, {
    encoding: "utf-8",
  });

  const legalConsultation = JSON.parse(legalConsultationData);

  legalConsultation.slug = "legal-consultation-b";

  const product = legalConsultation;

  delete product.seo;
  delete product.getTalkTime;

  const states = statesAllCountries.filter(
    (state) => state.countryCode === "IN"
  );

  const cities = citiesAllCountries
    .filter((city) => city.countryCode === "IN")
    .sort((a, b) => a.name.localeCompare(b.name));

  const concernedAreas = allConcernedAreas
    .filter(
      (concernedArea, index) =>
        concernedArea !== "Other Legal Problem" &&
        concernedArea !== "Other Property Issues" &&
        concernedArea !== "Any other Business Legal matter" &&
        allConcernedAreas.indexOf(concernedArea) === index
    )
    .sort();

  const CharArray = [{ label: "All", value: "" }].concat(
    ...Array.from(Array(26))
      .map((_, i) => i + 65)
      .map((x) => ({
        value: String.fromCharCode(x),
        label: String.fromCharCode(x),
      }))
  );

  return {
    props: {
      menus,
      HomePageData,
      footer,
      locations: {
        states,
        cities,
      },
      concernedAreas,
      product,
      CharArray,
    },
  };
};

export default ConcernArea;
